




import {
  defineComponent,
  nextTick,
  reactive,
  ref,
  watch
} from "@vue/composition-api";
import * as d3 from "d3";
import crossfilter from "crossfilter2";
import { Record } from "@/types";
import { CodeMatch } from "@/utils/encoding";
import { Document } from "@/types/documents";

interface Item {
  match: CodeMatch;
  document: Document;
}

interface TimelineRecord {
  name: string;
  date: Date;
  raw: Item;
}

interface Props {
  records: TimelineRecord[];
  range: [Date, Date] | null;
}

export default defineComponent<Props>({
  name: "TimelineData",
  props: {
    records: { type: Array, required: true },
    range: { type: Array }
  },
  setup(props, context) {
    const root = ref<HTMLDivElement | null>(null);

    let height = ref(600);
    const padding = reactive({ top: 30, bot: 20, left: 90, right: 15 });
    const PADDING_TOP_GRID = 5;

    const draw = () => {
      if (!root.value || !props.range) return;

      const filtered = crossfilter(props.records)
        .dimension(record => record.date)
        .filter(props.range)
        .top(Infinity);

      if (filtered.length === 0) {
        d3.select(root.value).html(
          `<div class="my-4 text-center">There are no data to be shown in the selected period.</div>`
        );
      }

      if (!props.records || !props.range || filtered.length === 0) return;

      const domain = crossfilter(filtered)
        .dimension(record => record.name)
        .group()
        .top(Infinity);

      const xScale = d3
        .scaleLinear()
        .domain(props.range)
        .range([padding.left, root.value.clientWidth - padding.right - 10]);

      const yScale = d3
        .scaleBand()
        .range([padding.top, padding.top + 30 * domain.length])
        .domain(domain.map(e => e.key as string))
        .paddingOuter(0.5);

      const canvas = d3
        .select(root.value)
        .html("")
        .append("svg")
        .attr("width", root.value.clientWidth)
        .attr("height", height.value)
        .style("overflow", "visible");

      const tooltip = d3
        .select(root.value)
        .append("div")
        .attr("class", "d-none");

      canvas
        .append("g")
        .attr("class", "x--axis")
        .attr("transform", "translate(0," + padding.top + ")")
        .call(
          d3
            .axisTop(xScale)
            .ticks(5)
            .tickFormat(d3.timeFormat("%Y %b %d") as any)
        );

      padding.left = Math.max(
        ...canvas
          .append("g")
          .attr("class", "grid y--axis")
          .attr("transform", `translate(${padding.left}, ${PADDING_TOP_GRID})`)
          .call(
            d3
              .axisLeft(yScale)
              .tickSize(-root.value.clientWidth + padding.left + padding.right)
          )
          .selectAll("text")
          .attr("transform", `translate(-5, -${yScale.bandwidth() / 2})`)
          .nodes()
          .map(el => (el as SVGTextElement).getBBox().width + 9)
      );

      canvas
        .selectAll(".data")
        .data(
          crossfilter(filtered)
            .dimension(d => d.name)
            .top(Infinity)
        )
        .enter()
        .append("circle")
        .attr("data-content", "Ok")
        .attr("style", "cursor: pointer")
        .attr("cx", d => xScale(d.date))
        .attr("cy", d => (yScale(d.name) || 0) + PADDING_TOP_GRID)
        .attr("r", 8)
        .attr("class", "text")
        .on(
          "click",
          (
            record: TimelineRecord,
            index: number,
            circles: SVGCircleElement[] | ArrayLike<SVGCircleElement>
          ) => {
            context.emit("select", record.raw);

            Array.from(circles).forEach(circle => (circle.style.stroke = "0"));

            circles[index].style.stroke = "rgba(0, 0, 0, 0.5)";
          }
        )
        .on("mouseover", record => {
          tooltip
            .html(
              `${record.date.toJSON().slice(0, 10)}`
            )
            .attr("class", "d-block p-1 position-absolute")
            .style("background-color", "black")
            .style("color", "white")
            .style("z-index", "9999")
            .style("top", `${d3.event.layerY - 5}px`)
            .style("left", `${d3.event.layerX}px`)
            .style("transform", "translate(-50%, -100%)");
        })
        .on("mouseout", () => {
          tooltip.attr("class", "d-none");
        });

      const node = canvas.node();

      if (node) {
        height.value = node.getBBox().height;
      }
    };

    watch(
      [
        () => props.records,
        () => props.range,
        () => height.value,
        () => padding
      ],
      () => draw()
    );

    return { root };
  }
});
